import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SessionUser } from '@fintracks/directus-sdk';
import { repository } from '../data/repository';

interface AuthValue { session: SessionUser | null; loading: boolean; login: (email: string, password: string) => Promise<void>; logout: () => Promise<void> }
const AuthContext = createContext<AuthValue | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    repository.currentUser()
      .then(user => { if (active) setSession(user); })
      .catch(() => { if (active) setSession(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const handleSessionExpired = () => setSession(null);
    window.addEventListener('fintracks:session-expired', handleSessionExpired);
    return () => window.removeEventListener('fintracks:session-expired', handleSessionExpired);
  }, []);
  const value = useMemo<AuthValue>(() => ({
    session, loading,
    login: async (email, password) => {
      try { setSession(await repository.login(email, password)); }
      catch (error) { setSession(null); throw error; }
    },
    logout: async () => {
      try { await repository.logout(); }
      finally { setSession(null); }
    },
  }), [session, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() { const value = useContext(AuthContext); if (!value) throw new Error('useAuth deve ser usado dentro de AuthProvider'); return value; }

// Telas reutilizadas em modo demo/teste podem ser renderizadas sem o provider.
// O caminho autenticado continua usando useAuth(), que falha fechado fora do shell.
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalAuth(): AuthValue | null { return useContext(AuthContext); }
